import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ImageNotSupportedOutlined from '@mui/icons-material/ImageNotSupportedOutlined';

// common
import { KIOS_STORAGE } from 'api/API_App';

// screen
import { useTabsData } from 'modules/suppliers/detail/context/useTabsData';
import { getSupplierExtras } from 'modules/suppliers/helpers/supplierExtras';

/** Ảnh KIOS có chỗ trả URL đầy đủ, có chỗ trả đường dẫn tương đối. */
const storageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  return `${(KIOS_STORAGE || '').replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
};

// App dùng breakpoint tuỳ biến (mobile/tablet/laptop/desktop), KHÔNG có xs/md mặc định.
// 2 cột chỉ bật từ laptop (1024px); màn nhỏ hơn xếp dọc để mã CĐS dài không bị wrap xấu.
const grid2 = {
  display: 'grid',
  gridTemplateColumns: { mobile: '1fr', laptop: '1fr 1fr' },
  gap: 2,
} as const;

const LabeledValue: FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 600 }}>{value || '-'}</Typography>
  </Box>
);

/**
 * Hai khối đứng ĐẦU trang chi tiết HSNL NCC, dùng chung cho Quick Search và QR NCC TPAT:
 * "Thông tin NCC tham chiếu" + "Thông tin cơ bản HSNL".
 *
 * Đặt ngoài TabsView (không nằm trong tab "Thông tin chung") theo đúng thiết kế — nhờ vậy chuyển
 * sang tab "Loại hàng hoá" vẫn còn thấy đang xem hồ sơ của ai.
 */
const SupplierRefCards: FC = () => {
  const { formatMessage } = useIntl();
  const { generalDetails } = useTabsData();

  if (!generalDetails) return null;

  const ref = generalDetails.referencedSupplier;
  // Ảnh/mô tả chỉ có ở API danh sách → đọc lại từ localStorage (lưu khi bấm xem).
  const extras = getSupplierExtras(generalDetails.operationCode, generalDetails.id);
  const label = (id: string) => formatMessage({ id: `supplier.refCards.${id}` });

  return (
    <Box sx={{ ...grid2, mb: 3 }}>
      {/* Thông tin NCC tham chiếu */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
          {label('refTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Avatar
            variant="rounded"
            src={storageUrl(ref?.logo) || undefined}
            sx={{ width: 88, height: 88, bgcolor: 'action.hover', color: 'text.disabled' }}
          >
            <ImageNotSupportedOutlined />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>{ref?.name || '-'}</Typography>
            <Box sx={{ ...grid2, gap: 1 }}>
              <LabeledValue label={label('operationCode')} value={ref?.operationCode} />
              <Box />
              <LabeledValue
                label={label('businessRegistrationNumber')}
                value={ref?.businessRegistrationNumber}
              />
              <LabeledValue
                label={label('businessRegistrationType')}
                value={ref?.businessRegistrationTypeName}
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Thông tin cơ bản HSNL */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
          {label('hsnlTitle')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}
          >
            {storageUrl(extras.image) ? (
              <Box
                component="img"
                src={storageUrl(extras.image)}
                alt=""
                sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 1 }}
              />
            ) : (
              <Avatar variant="rounded" sx={{ width: 120, height: 120, bgcolor: 'action.hover' }}>
                <ImageNotSupportedOutlined />
              </Avatar>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {label('productImage')}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: 13, mb: 0.5 }}>
              {label('productDescription')}
              <Box component="span" sx={{ color: 'error.main' }}>
                {' *'}
              </Box>
            </Typography>
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                minHeight: 96,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {extras.description || ''}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SupplierRefCards;
